import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Maybe } from "true-myth";

import { IOption } from "components/shared/dropdown";
import Modal from "components/shared/modal";
import Tablist, { TabPanel } from "components/shared/tablist";
import TimeBlockForm, {
  IFormValues,
  DEFAULT_ERRORS,
} from "components/schedule/time_block_form";
import TimeBlockButtons from "components/schedule/time_block_buttons";

import { INewShift, BlockActionTypes } from "actions/scheduling/blocks";
import { anyValue } from "helpers/collections";
import {
  ExistingBlock,
  getInitialValues,
  newUnavailFromFormValues,
  newShiftFromFormValues,
  IEmployeeDropdownOption,
  IRoleDropdownOption,
  TOGGLE_MODES,
  TAB_BUTTONS,
  validateFormValues,
} from "helpers/schedule/time_block_modal";
import { isBlockUnavail } from "helpers/schedule/shifts";
import {
  ISchedule,
  IPosUser,
  IShift,
  IPerson,
  IUnavailabilityBlock,
} from "types/schedules";
import { LDFlags } from "interfaces/context";

export interface IBlockModalData {
  existingBlock: ExistingBlock;
  person: IPerson;
  date: Date;
  defaultHour?: number;
  confirmingDelete?: boolean;
}
interface ITimeBlockModal {
  allEmployeeOptions: IEmployeeDropdownOption[];
  allRoleOptions: IRoleDropdownOption[];
  schedule: ISchedule;
  scheduleDates: Array<IOption<Date>>;
  // Actions
  closeBlockModal: () => void;
  deleteShift: (shift: IShift, assignee: Maybe<IPosUser>) => void;
  deleteUnavail: (unuvail: IUnavailabilityBlock) => void;
  saveShift: (
    shift: INewShift,
    existingShift: Maybe<IShift>,
    existingAssignee: Maybe<IPosUser>
  ) => BlockActionTypes;
  saveUnavail: (unavail: IUnavailabilityBlock) => void;
  saveUnavailException: (exception: IUnavailabilityBlock) => void;
  featureFlags: LDFlags;
}

function TimeBlockModal({
  allEmployeeOptions,
  allRoleOptions,
  closeBlockModal,
  confirmingDelete = false,
  existingBlock,
  date,
  defaultHour = 12,
  person,
  schedule,
  scheduleDates,
  saveShift,
  saveUnavail,
  deleteShift,
  deleteUnavail,
  saveUnavailException,
  featureFlags,
}: ITimeBlockModal & IBlockModalData) {
  const initValues: IFormValues = useMemo(
    () =>
      getInitialValues({
        allEmployeeOptions,
        allRoleOptions,
        date,
        defaultHour,
        existingBlock,
        person,
        scheduleDates,
      }),
    [] // one time only
  );
  const [formValues, setFormValues] = useState(initValues);

  // If there is an existing block (shift or unavail), initialize modal in that corresponding mode
  const isExistingUnavail = existingBlock.match({
    Just: isBlockUnavail, // only unavails have frequency field
    Nothing: () => false,
  });
  const [toggleMode, setToggleMode] = useState(
    isExistingUnavail ? TOGGLE_MODES.UNAVAILABLE : TOGGLE_MODES.SHIFT
  );
  const changeMode = useCallback(
    (newMode: string) => {
      setToggleMode(newMode as TOGGLE_MODES);
    },
    [setToggleMode]
  );

  // We're creating a new block when no existing block was given
  const isCreating = existingBlock.isNothing();
  const isUnavail = toggleMode === TOGGLE_MODES.UNAVAILABLE;

  // When deleting recurring unavailability, there is an additional confirmation step,
  // to delete full series or single instance.
  const [isConfirmingDelete, setConfirmingDelete] = useState(confirmingDelete);

  let title;
  if (isConfirmingDelete) {
    title = "Which do you want to delete?";
  } else {
    const blockType = isUnavail ? "unavailability" : "shift";
    title = `${isCreating ? "Add new" : "Edit"} ${blockType}`;
  }

  // Errors
  const [errors, setErrors] = useState(DEFAULT_ERRORS);
  const [canOverrideError, setOverride] = useState(false);
  const [displayErrors, setDisplayErrors] = useState(false);
  // When there are errors, prevent submission by disabling button - form is not validated.
  // However, if the error is an overridable conflict, it can be ignored as long as its been displayed.
  const isValidated =
    // @ts-expect-error TODO please fix this error
    canOverrideError && errors.conflict ? displayErrors : !anyValue(errors);

  // Error state is shared between both modes but is recalculated whenever there's a change.
  useEffect(
    () => {
      const [newErrors, isErrorOverridable] = validateFormValues(
        isUnavail,
        existingBlock,
        formValues
      );
      setErrors(newErrors);
      setOverride(isErrorOverridable);
    },
    [isUnavail, existingBlock, formValues]
  );

  // Form Action SAVE
  const onSave = useCallback(
    () => {
      if (!isValidated) {
        setDisplayErrors(true);
      } else if (isUnavail) {
        const maybeNewUnavail = newUnavailFromFormValues(
          formValues,
          existingBlock
        );
        maybeNewUnavail.map(newUnavail => {
          saveUnavail(newUnavail);
          closeBlockModal();
          return newUnavail;
        });
      } else {
        const maybeNewShift = newShiftFromFormValues(formValues, existingBlock);
        maybeNewShift.map(newShift => {
          saveShift(newShift, existingBlock as Maybe<IShift>, person.assignee);
          closeBlockModal();
          return newShift;
        });
      }
    },
    [formValues, isUnavail, isValidated, saveShift, saveUnavail]
  );

  const buttons = (
    <TimeBlockButtons
      disableSave={displayErrors && !isValidated}
      existingBlock={existingBlock}
      formValues={formValues}
      isExistingUnavail={isExistingUnavail}
      isConfirmingDelete={isConfirmingDelete}
      isUnavail={isUnavail}
      onCancel={closeBlockModal}
      person={person}
      deleteShift={deleteShift}
      deleteUnavail={deleteUnavail}
      saveUnavailException={saveUnavailException}
      setConfirmingDelete={setConfirmingDelete}
    />
  );

  // When confirming delete, just show the buttons.
  // Otherwise, show the full form
  let content;
  if (isConfirmingDelete) {
    content = buttons;
  } else {
    content = (
      <TimeBlockForm
        errors={displayErrors ? errors : DEFAULT_ERRORS}
        isUnavail={isUnavail}
        isCreating={isCreating}
        initValues={initValues}
        allEmployeeOptions={allEmployeeOptions}
        allRoleOptions={allRoleOptions}
        schedule={schedule}
        scheduleDates={scheduleDates}
        onChange={setFormValues}
        onSave={onSave}>
        {buttons}
      </TimeBlockForm>
    );
  }

  return (
    <Modal title={title} onClose={closeBlockModal}>
      {isCreating && (
        <Tablist
          label="Creating"
          onChange={changeMode}
          tabButtons={TAB_BUTTONS}
        />
      )}
      <TabPanel tabId={toggleMode}>{content}</TabPanel>
    </Modal>
  );
}

// Return a Switch component by default,
// which only renders when blockModalData is provided
interface ISwitch extends ITimeBlockModal {
  blockModalData: Maybe<IBlockModalData>;
}

function TimeBlockModalSwitch({ blockModalData, ...rest }: ISwitch) {
  return blockModalData.match({
    Just: data => <TimeBlockModal {...data} {...rest} />,
    Nothing: () => null,
  });
}

export default TimeBlockModalSwitch;
